import React, { useState, useEffect } from 'react';

// MUI
import {
  Button,
  Drawer,
  Box,
  Typography,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  IconButton,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import RenameSourceAndReferences from './RenameSourceAndReferences';
import FixBrokenPlaceholders from './FixBrokenPlaceholders';
import { DropdownLabel } from './RenameStyledComponents';

function RenamePlaceholdersDialog({
  isOpen,
  closeDialog,
  projectId,
  openBrokenPHRename,
  setOpenBrokenPHRename,
  renameGivenPH,
  setRenameGivenPH,
  openInPane,
  forceCloseComponentPanes,
}) {
  const [isInitialSelection, setIsInitialSelection] = useState(true);
  const [renameOption, setRenameOption] = useState('');

  useEffect(() => {
    if (!openBrokenPHRename) return;

    // Need to open broken PH replace if given
    setRenameOption('broken_references');
    setIsInitialSelection(false);

    return () => {
      // Clean up
      setIsInitialSelection(true);
      setRenameOption('');
      setOpenBrokenPHRename(false);
    };
  }, []);

  const getStartButtons = () => {
    return (
      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button
          variant="contained"
          onClick={() => setIsInitialSelection(false)}
          fullWidth
          disabled={!renameOption}
        >
          Next
        </Button>
      </Box>
    );
  };

  const getSelectDropdown = () => {
    const descriptions = {
      source_and_references:
        'This option allows you to rename an existing placeholder and automatically update all references to it throughout your project. Use this when you want to update all references to one placeholder to another, or to rename a placeholder from one name to another.',
      broken_references:
        'This option helps you fix broken placeholder references by pointing them to an existing valid placeholder. Use this when you have references to placeholders that no longer exist.',
    };

    return (
      <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h6" component="h2">
            Rename and Fix Placeholders
          </Typography>
          <IconButton onClick={closeDialog} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ flex: 1 }}>
          <FormControl fullWidth>
            <RadioGroup value={renameOption} onChange={e => setRenameOption(e.target.value)}>
              <FormControlLabel
                value="source_and_references"
                control={<Radio />}
                label="Update placeholder name to a new name"
              />
              <FormControlLabel
                value="broken_references"
                control={<Radio />}
                label="Update broken placeholders to another placeholder"
              />
            </RadioGroup>
            {renameOption && (
              <DropdownLabel sx={{ mt: 2 }}>{descriptions[renameOption]}</DropdownLabel>
            )}
          </FormControl>
        </Box>

        {getStartButtons()}
      </Box>
    );
  };

  const getRelevantOption = () => {
    const handleBack = () => setIsInitialSelection(true);

    if (renameOption === 'source_and_references')
      return (
        <RenameSourceAndReferences
          projectId={projectId}
          closeDialog={closeDialog}
          onBack={handleBack}
          openInPane={openInPane}
          forceCloseComponentPanes={forceCloseComponentPanes}
        />
      );
    else if (renameOption === 'broken_references')
      return (
        <FixBrokenPlaceholders
          projectId={projectId}
          closeDialog={closeDialog}
          onBack={handleBack}
          openInPane={openInPane}
          forceCloseComponentPanes={forceCloseComponentPanes}
        />
      );
  };

  return (
    <Drawer
      anchor="left"
      open={isOpen ? isOpen : false}
      onClose={closeDialog}
      hideBackdrop={true}
      variant="persistent"
      PaperProps={{
        sx: {
          width: 500,
          maxWidth: '90vw',
          left: 48, // Offset to not overlap with main navigation drawer
          zIndex: 1000, // Lower than main navigation drawer
        },
      }}
    >
      {isInitialSelection ? getSelectDropdown() : getRelevantOption()}
    </Drawer>
  );
}

export default RenamePlaceholdersDialog;
